/** Guía rápida del administrador (Eduardo 2026-08-23: "más interactiva,
 * con capturas de pantalla mostrando cada sección").
 *
 * UNA sola fuente para las tres superficies: la página /admin/guia, el
 * botón "?" contextual del dock y la versión pública /guia. Las capturas
 * viven en frontend/public/guia/<screenshot>; si el archivo aún no existe,
 * la página muestra un marco de "captura pendiente" sin romperse. */

export interface GuideSection {
  key: string;
  /** Número de paso mostrado (orden del torneo). */
  order: number;
  title: string;
  icon: string;
  /** Para qué sirve (1 línea). */
  purpose: string;
  /** Qué haces aquí (3–5 pasos). */
  steps: string[];
  /** Ojo con… (trampas reales de operación). */
  warnings: string[];
  /** Listo cuando… (criterio para avanzar). */
  doneWhen: string;
  /** Archivo bajo public/guia/. */
  screenshot: string;
  /** Ancla en la guía ilustrada (/guia-admin.html); null = sin sección
   * equivalente (el link cae al inicio de la guía). */
  illustratedAnchor: string | null;
  /** Ruta dentro del torneo (relativa a /admin/tournaments/:id) para el
   * botón "Ir a esta sección"; null = fuera del contexto de torneo. */
  tournamentPath: string | null;
  /** Prefijos de ruta que mapean esta sección para el "?" contextual. */
  routePrefixes: string[];
}

/** Guía ilustrada completa (Cowork, 78 capturas embebidas): un solo HTML
 * autocontenido servido como asset estático desde public/. Vive en la raíz
 * del dist, así que su URL sigue al BASE_URL (/tenis/ en producción — las
 * páginas van en la raíz pero los archivos reales se sirven bajo /tenis/). */
export const ILLUSTRATED_GUIDE_URL = `${import.meta.env.BASE_URL}guia-admin.html`;

export function illustratedGuideUrl(section: GuideSection): string {
  return section.illustratedAnchor
    ? `${ILLUSTRATED_GUIDE_URL}#${section.illustratedAnchor}`
    : ILLUSTRATED_GUIDE_URL;
}

export const ADMIN_GUIDE_SECTIONS: GuideSection[] = [
  {
    key: "datos",
    order: 1,
    title: "Datos del torneo",
    icon: "⚙️",
    purpose: "La identidad de tu torneo: alimenta la convocatoria, el link público, los PDFs y la credencial.",
    steps: [
      "Captura nombre, sede y link de Google Maps; agrega más sedes si juegas en varios lugares.",
      "Define fechas del torneo y cierre de inscripciones.",
      "Pon el Costo Normal (y Socio/Profesores si aplican: el jugador elegirá su tipo al inscribirse).",
      "Elige la forma de cobro — Mercado Pago; cobrar con comprobantes requiere autorización de Torneos123.",
      "Sube tu identidad: nombre de la empresa, póster del torneo, logo del organizador y banners de patrocinador.",
    ],
    warnings: [
      "El póster se muestra en grande: súbelo en buena resolución (hasta 5 MB).",
      "El link del grupo de WhatsApp solo se muestra al jugador cuando su pago está confirmado.",
      "Si cambias el logo después de emitir credenciales, regenéralas.",
    ],
    doneWhen: "El torneo tiene nombre, fechas, costo y forma de cobro — y está marcado como público si quieres recibir inscripciones.",
    screenshot: "guia-01-datos.png",
    illustratedAnchor: "datos",
    tournamentPath: "/edit",
    routePrefixes: ["/edit", "/setup/settings", "/settings"],
  },
  {
    key: "reglamento",
    order: 2,
    title: "Reglamento",
    icon: "📄",
    purpose: "Las reglas que el jugador acepta al inscribirse.",
    steps: [
      "Llena los campos: inscripciones y pagos, conducta, logística y aclaraciones.",
      "Presiona Guardar: queda visible de inmediato en el link público (no hay paso aparte de publicar).",
      "Usa Ver en PDF para revisarlo o compartirlo.",
      "Opcional: sube tu propio PDF si ya tienes un reglamento con diseño.",
    ],
    warnings: [
      "El jugador acepta el reglamento UNA sola vez al inscribirse; corregirlo después no le vuelve a preguntar.",
    ],
    doneWhen: "Guardaste al menos los campos principales.",
    screenshot: "guia-02-reglamento.png",
    illustratedAnchor: "reglamento",
    tournamentPath: "/setup/rules",
    routePrefixes: ["/rules", "/setup/rules"],
  },
  {
    key: "convocatoria",
    order: 3,
    title: "Convocatoria",
    icon: "📣",
    purpose: "La información pública del torneo, lista para compartir.",
    steps: [
      "Revisa que fechas, sede, costo y pelota oficial sean correctos (vienen de Datos).",
      "Genera el PDF de convocatoria con tu logo y compártelo por WhatsApp.",
    ],
    warnings: [
      "Los mapas salen como \"Link\" en el PDF para que no se descuadre — el vínculo sigue siendo clickeable.",
    ],
    doneWhen: "El PDF dice exactamente lo que quieres que vean los jugadores.",
    screenshot: "guia-03-convocatoria.png",
    illustratedAnchor: "convocatoria",
    tournamentPath: "/setup/call",
    routePrefixes: ["/setup/call"],
  },
  {
    key: "inscripciones",
    order: 4,
    title: "Inscripciones",
    icon: "💳",
    purpose: "Aquí llegan los jugadores y aquí controlas quién ya pagó.",
    steps: [
      "Comparte el link público de inscripción (botón Copiar link).",
      "Revisa cada registro: documentos, pago y categoría.",
      "¿Alguien llegó al mostrador? Inscripción rápida: nombre, categorías y cobro en un paso.",
      "¿Se declaró Socio y no lo es? Cámbiale el Tipo de jugador: lo pagado se respeta y la diferencia queda como saldo pendiente.",
    ],
    warnings: [
      "Si el draw ya existe y siguen llegando inscritos, la app avisa \"fuera del draw\": agrégalos y regenera.",
      "El saldo de una reclasificación se marca Pagado o se Condona desde la misma fila.",
    ],
    doneWhen: "Todos los que van a jugar están inscritos con pago confirmado.",
    screenshot: "guia-04-inscripciones.png",
    illustratedAnchor: "inscripciones",
    tournamentPath: "/registrations",
    routePrefixes: ["/registrations", "/setup/registrations"],
  },
  {
    key: "pagos",
    order: 5,
    title: "Cobros y Mercado Pago",
    icon: "🏦",
    purpose: "Cobrar en línea sin perseguir a nadie.",
    steps: [
      "En Pagos, conecta tu cuenta de Mercado Pago una sola vez: sirve para todos tus torneos.",
      "El jugador paga con tarjeta, OXXO o SPEI y su inscripción se marca pagada sola.",
      "El costo de plataforma se separa automáticamente en cada pago.",
    ],
    warnings: [
      "Cobrar con comprobantes es caso excepcional: se habilita torneo por torneo con Torneos123.",
      "Un torneo cobrado por fuera no puede iniciar hasta cubrir o condonar el costo de plataforma.",
    ],
    doneWhen: "Tu cuenta de Mercado Pago está conectada y el torneo tiene forma de cobro definida.",
    screenshot: "guia-05-pagos.png",
    illustratedAnchor: "inscripciones",
    tournamentPath: null,
    routePrefixes: [],
  },
  {
    key: "jugadores",
    order: 6,
    title: "Categorías y jugadores",
    icon: "👥",
    purpose: "Organizar a los inscritos por categoría.",
    steps: [
      "Crea las categorías (una categoría puede tener costo propio).",
      "Un jugador puede jugar una, dos o más categorías en el mismo torneo.",
      "Da de alta, cambia de categoría o da de baja desde aquí.",
    ],
    warnings: [
      "Revisa que cada jugador esté en su categoría correcta antes de generar grupos.",
    ],
    doneWhen: "Cada categoría tiene a sus jugadores definitivos.",
    screenshot: "guia-06-jugadores.png",
    illustratedAnchor: "jugadores",
    tournamentPath: "/players",
    routePrefixes: ["/players", "/setup/players"],
  },
  {
    key: "draw",
    order: 7,
    title: "Grupos y Draw",
    icon: "🎾",
    purpose: "Armar la competencia: grupos o cuadro de eliminación.",
    steps: [
      "Genera los grupos o el draw por categoría; ajusta a mano si hace falta.",
      "Los descansos aparecen como BYE.",
      "Si llegan jugadores nuevos, verás el aviso rojo de \"fuera del draw\": regenera para incluirlos.",
    ],
    warnings: [
      "Regenerar reacomoda el cuadro: hazlo antes de programar partidos.",
    ],
    doneWhen: "Cada categoría tiene su cuadro y no hay inscritos fuera del draw.",
    screenshot: "guia-07-draw.png",
    illustratedAnchor: "formato",
    tournamentPath: "/rounds",
    routePrefixes: ["/rounds", "/groups", "/setup/groups", "/setup/rounds"],
  },
  {
    key: "calendario",
    order: 8,
    title: "Calendario",
    icon: "📅",
    purpose: "Cuándo y dónde se juega cada partido.",
    steps: [
      "Al abrir verás la jornada actual (la de las fechas de hoy; al inicio, la Jornada 1). En el selector puedes elegir «Todas».",
      "Define canchas por sede y programa los partidos, uno a uno o en bloque.",
      "Genera el PDF del calendario con tu logo para compartirlo.",
      "Al armar el torneo, al final de Calendario (paso 10) está «Publicar el torneo».",
    ],
    warnings: [
      "Si el torneo no usa calendario de canchas, los jugadores se coordinan entre ellos y aquí solo consultas.",
    ],
    doneWhen: "La jornada próxima tiene todos sus partidos con fecha, hora y cancha.",
    screenshot: "guia-08-calendario.png",
    illustratedAnchor: "calendario",
    tournamentPath: "/schedule",
    routePrefixes: ["/schedule", "/calendar"],
  },
  {
    key: "resultados",
    order: 9,
    title: "Resultados",
    icon: "✅",
    purpose: "Capturar marcadores y mantener posiciones al día.",
    steps: [
      "Captura el resultado de cada partido, o revisa los que reportan los jugadores.",
      "Posiciones y ranking se actualizan solos; al cerrar la ronda, el draw avanza.",
    ],
    warnings: [
      "Un resultado mal capturado se corrige desde el mismo partido.",
    ],
    doneWhen: "No quedan partidos pendientes de la jornada.",
    screenshot: "guia-09-resultados.png",
    illustratedAnchor: "resultados",
    tournamentPath: "/results",
    routePrefixes: ["/results", "/result-reports"],
  },
  {
    key: "credenciales",
    order: 10,
    title: "Credenciales",
    icon: "🎫",
    purpose: "La credencial oficial del jugador, con QR verificable.",
    steps: [
      "Usa Ver credencial muestra para revisar el formato con tu identidad antes de emitir.",
      "Genera la credencial de cada jugador desde su registro (necesita su foto).",
      "El jugador la ve y comparte desde su propia pantalla.",
    ],
    warnings: [
      "Las credenciales son archivos: si cambias logo o empresa en Datos, regenera las emitidas.",
    ],
    doneWhen: "La muestra se ve como quieres.",
    screenshot: "guia-10-credenciales.png",
    illustratedAnchor: "credenciales",
    tournamentPath: "/setup/registrations",
    routePrefixes: [],
  },
  {
    key: "equipo",
    order: 11,
    title: "Equipo",
    icon: "🤝",
    purpose: "Dar acceso a tu gente sin compartir tu contraseña.",
    steps: [
      "Crea sub-usuarios (hasta 10) en Equipo y elige su perfil.",
      "Elige a qué torneos entra cada quien: todos tus torneos (también los que crees después) o solo los que marques.",
      "Coadministrador: todo lo operativo, sin borrar el torneo ni tocar cobros o el equipo.",
      "Capturista: solo programar partidos y capturar resultados.",
      "Finanzas: solo inscripciones y pagos.",
    ],
    warnings: [
      "Solo el dueño del torneo administra el equipo.",
      "El mismo sub-usuario puede ayudarte en varios torneos sin contar doble.",
    ],
    doneWhen: "Cada colaborador tiene su acceso con el perfil correcto.",
    screenshot: "guia-11-equipo.png",
    illustratedAnchor: null,
    tournamentPath: "/team",
    routePrefixes: ["/team"],
  },
  {
    key: "comunicados",
    order: 12,
    title: "Comunicados y cierre",
    icon: "🔔",
    purpose: "Hablar con tus jugadores y cerrar bien el torneo.",
    steps: [
      "Publica avisos: los jugadores los ven en su pantalla del torneo.",
      "Al terminar, cierra el torneo desde el Resumen: queda en solo lectura, se guarda el campeón y el lugar de cada jugador, y puedes mandar el comunicado de campeones.",
      "¿Lo repetirás? «Duplicar torneo» (en el Resumen o en la tarjeta de Torneos) crea un borrador con las mismas categorías, formulario de inscripción, costos, reglamento y patrocinadores; tú solo eliges nombre y fechas y subes el póster nuevo. No se copian jugadores, pagos, grupos de WhatsApp, el PDF propio de la convocatoria, la liga a un ranking ni resultados, y el nuevo torneo no sale en torneos123.com hasta que marques «Torneo público».",
    ],
    warnings: [],
    doneWhen: "El torneo está cerrado y cada jugador ve su resultado final.",
    screenshot: "guia-12-comunicados.png",
    illustratedAnchor: "comunicados",
    tournamentPath: "/communications",
    routePrefixes: ["/communications", "/announcements"],
  },
];

const TOURNAMENT_PATH_RE = /^\/admin\/tournaments\/(\d+)(\/.*)?$/;

/** Sección de la guía que corresponde a la ruta actual (para el "?"). */
export function guideSectionForPath(pathname: string): GuideSection | null {
  const match = pathname.match(TOURNAMENT_PATH_RE);
  if (!match) return null;
  const rest = match[2] ?? "";
  for (const section of ADMIN_GUIDE_SECTIONS) {
    if (section.routePrefixes.some((prefix) => rest === prefix || rest.startsWith(`${prefix}/`))) {
      return section;
    }
  }
  return null;
}
